import {
  useGetFailedOfficesJobsQuery,
  useGetFailedOfficesRecordsQuery,
  useLazyDownloadOfficesTemplateQuery,
  useUploadOfficesMutation,
} from '@/app/features/control-panel/organization/organization.api';
import type { FailedOfficeRecord } from '@/app/features/control-panel/organization/organization.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedOfficeRecord>['columns'] = [
  {
    title: 'Row Number',
    dataIndex: 'row_number',
    width: 100,
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Office Name',
    dataIndex: 'office_name',
    render: (val) =>
      val ? <span className="block min-w-[120px]">{val}</span> : 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function BulkOfficesUpload({ open, handleClose }: Props) {
  const [uploadOffices, { isLoading: isUploading }] = useUploadOfficesMutation();
  const [downloadTemplate] = useLazyDownloadOfficesTemplateQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'offices-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadOffices(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Office CSV Upload"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedOfficeRecord>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedOfficesJobsQuery}
        useGetRecordsQuery={useGetFailedOfficesRecordsQuery}
        columns={columns}
        jobsErrorMessage="Failed to get office import error data"
        recordsErrorMessage="Failed to get office import error data"
      />
    </>
  );
}
